import type { Location } from "@/content/locations";
import type { ProgrammableServiceSlug } from "@/content/city-services";

/** Copy for the programmable city × service pages, keyed by service slug. */

/** AI-answer question slugs shown on each city × service page. */
export const SERVICE_QUESTIONS: Record<ProgrammableServiceSlug, string[]> = {
  "local-seo": [
    "local-seo-still-matter",
    "seo-bangladesh-market",
    "bangla-seo-worth-it",
    "seo-cost-bangladesh",
  ],
  "technical-seo-audit": [
    "core-web-vitals-2026",
    "javascript-seo-safe",
    "what-schema-for-aeo",
    "block-ai-crawlers",
  ],
  "ecommerce-seo": [
    "what-schema-for-aeo",
    "get-cited-by-chatgpt",
    "measure-aeo-citations",
    "core-web-vitals-2026",
  ],
  "aeo-geo": [
    "get-cited-by-chatgpt",
    "aeo-vs-seo-vs-geo",
    "what-is-google-ai-overviews",
    "aeo-time-to-results",
  ],
  "content-strategy": [
    "what-is-aeo",
    "faqpage-schema-worth-it",
    "how-chatgpt-picks-citations",
    "measure-aeo-citations",
  ],
};

/** One-sentence city-specific positioning for each service (also the schema description). */
export const SERVICE_ANGLE: Record<ProgrammableServiceSlug, (loc: Location) => string> = {
  "local-seo": (loc) =>
    `Google Business Profile, map-pack rankings, citations, and review velocity built for ${loc.city} — the fastest way for local buyers to find you first.`,
  "technical-seo-audit": (loc) =>
    `A crawl-first, Core Web Vitals audit for ${loc.city} sites — surfacing the render, indexation, and schema bugs quietly suppressing your rankings.`,
  "ecommerce-seo": (loc) =>
    `Category, collection, and product SEO for ${loc.city} e-commerce brands — schema, faceted nav, and merchandising that turn pages into compounding revenue.`,
  "aeo-geo": (loc) =>
    `Get cited by ChatGPT, Perplexity, Gemini, and Google AI Overviews for ${loc.city}-intent queries — entity SEO, structured answers, and llms.txt strategy.`,
  "content-strategy": (loc) =>
    `Topical maps, SERP-driven briefs, and editor-grade production for ${loc.city} brands — content that ranks, converts, and refreshes on schedule.`,
};

/** Deliverables listed for each service, phrased for the city. */
export const SERVICE_PILLARS: Record<ProgrammableServiceSlug, (loc: Location) => string[]> = {
  "local-seo": (loc) => [
    `Full Google Business Profile rebuild for the ${loc.city} address & service areas`,
    `NAP citation cleanup across Bangladesh-relevant directories`,
    `Neighborhood landing pages for ${loc.neighborhoods.slice(0, 3).join(", ")} and more`,
    `Ethical, TOS-safe review generation tuned to ${loc.city} buyer behavior`,
  ],
  "technical-seo-audit": (loc) => [
    `Full crawl + log-file analysis benchmarked against ${loc.city} SERP leaders`,
    `Core Web Vitals report with mobile-first fixes for BD network conditions`,
    `Schema, hreflang, canonical & indexation review`,
    `Engineer-ready fix tickets prioritized by ranking impact`,
  ],
  "ecommerce-seo": (loc) => [
    `Category & collection keyword mapping around ${loc.city} buyer intent`,
    `Faceted nav, canonical & crawl-budget strategy for Shopify / WooCommerce`,
    `Product, review & FAQ schema for rich-result CTR lift`,
    `Bangla + English merchandising for ${loc.city}'s bilingual buyers`,
  ],
  "aeo-geo": (loc) => [
    `Entity & brand knowledge-graph build-out anchored in ${loc.city}, ${loc.country}`,
    `Citation-friendly content and schema markup written for LLM ingestion`,
    `AI visibility tracking across ChatGPT, Perplexity, and Gemini`,
    `llms.txt + AI-crawler access policy for your ${loc.city} site`,
  ],
  "content-strategy": (loc) => [
    `Topical map & content cluster plan for ${loc.city}'s primary industries`,
    `SERP-driven briefs and editorial guidelines`,
    `Long-form article & landing-page production`,
    `Decay-recovery program that refreshes existing ${loc.city} content`,
  ],
};

/**
 * City-specific questions layered on top of the generic service FAQs. These
 * are the questions buyers in each city actually ask on a first call, so the
 * page answers local intent instead of repeating the national service page.
 */
export const LOCAL_FAQS: Record<
  ProgrammableServiceSlug,
  (loc: Location) => { q: string; a: string }[]
> = {
  "local-seo": (loc) => [
    {
      q: `How much does local SEO cost in ${loc.city}?`,
      a: `It depends on how competitive your category is, how many locations you have, and whether the Google Business Profile needs rebuilding from scratch. I quote after a short review of your profile and competitors, and you get the scope and price in writing before anything starts. I don't publish a fixed ${loc.city} price because the work varies too much.`,
    },
    {
      q: `How long before I show up in the ${loc.city} map pack?`,
      a: `There's no guaranteed timeline. It depends on your category, competitors, distance from searchers, and how many genuine reviews you earn. Fixing the profile, categories, photos and citations comes first; I then track your map position monthly and report what changed, rather than promising a date.`,
    },
    {
      q: `Do you work with businesses outside central ${loc.city}?`,
      a: `Yes. I cover ${loc.neighborhoods.slice(0, 4).join(", ")} and the wider ${loc.region} service area. Everything runs remotely with a monthly call, and in-person sessions are possible for ${loc.city}-based clients.`,
    },
  ],
  "technical-seo-audit": (loc) => [
    {
      q: `What does a technical SEO audit cost for a ${loc.city} business?`,
      a: `A full 200-point audit for a typical ${loc.city} site runs as a fixed-fee project, sized by page count. Small brochure sites are quick; e-commerce catalogues with faceted navigation take longer because crawl-budget and indexation work is the bulk of the job.`,
    },
    {
      q: `Do you fix the issues or just report them?`,
      a: `Both options exist. The default deliverable is engineer-ready tickets your ${loc.city} developer can ship. If you don't have a developer, I implement the fixes directly on WordPress, Shopify, Webflow, or a custom stack.`,
    },
    {
      q: `Will the audit account for slow mobile networks in ${loc.country}?`,
      a: `Yes — Core Web Vitals are measured against mid-range Android devices on 4G, which is how most of your ${loc.city} traffic actually arrives. Desktop-only scores hide the problems that cost you rankings here.`,
    },
  ],
  "ecommerce-seo": (loc) => [
    {
      q: `Do you work with ${loc.city} stores on Shopify and WooCommerce?`,
      a: `Yes — most ${loc.city} clients are on Shopify, WooCommerce, or a custom Laravel build. The collection strategy, schema, and internal linking work is the same across all three; only the implementation details change.`,
    },
    {
      q: `Should my product pages be in Bangla or English?`,
      a: `For ${loc.city} buyers, the honest answer is usually both. English carries the commercial keywords and Bangla wins the long-tail and voice searches. I map which categories deserve bilingual pages instead of translating everything.`,
    },
    {
      q: `Can e-commerce SEO compete with Daraz and Facebook shops?`,
      a: `On broad terms, marketplaces will outrank you. The winnable ground is category depth, buying guides, brand terms, and ${loc.city}-intent delivery queries — that's where the margin-friendly orders come from.`,
    },
  ],
  "aeo-geo": (loc) => [
    {
      q: `Do AI assistants recommend ${loc.city} businesses?`,
      a: `Increasingly, yes. ChatGPT, Perplexity, and Google AI Overviews answer "best <service> in ${loc.city}" style prompts by pulling from structured, well-cited pages and consistent entity signals. Very few ${loc.city} brands optimise for it, which is exactly the opening.`,
    },
    {
      q: `How do you track AI citations for a ${loc.city} brand?`,
      a: `I run a fixed prompt set covering your ${loc.city} services, categories, and competitor comparisons across ChatGPT, Perplexity, and Gemini every month, then report which prompts cite you, which cite a competitor, and what changed.`,
    },
    {
      q: `Is AEO worth it if I already rank on Google in ${loc.city}?`,
      a: `Yes — the same content earns both, but AI answers strip the click. If you're already ranking, AEO is mostly structure and entity work on pages you own, which makes it one of the cheapest wins available.`,
    },
  ],
  "content-strategy": (loc) => [
    {
      q: `How much content does a ${loc.city} business actually need?`,
      a: `Fewer, deeper pieces beat volume. For most ${loc.city} brands, four to eight properly-briefed articles a month inside two or three clusters outperforms daily thin posts — and it's cheaper to maintain.`,
    },
    {
      q: `Do you write in Bangla as well as English?`,
      a: `Yes. Bangla content is written natively, not machine-translated, and paired with the English version through hreflang so both rank instead of competing.`,
    },
    {
      q: `Who writes the content — you or an agency team?`,
      a: `Briefs, structure, and final edit are mine. Drafting is shared with a small vetted team, and nothing publishes without a senior pass against the ${loc.city} search intent it was commissioned for.`,
    },
  ],
};
